<template>
  <div class="min-h-screen bg-gray-50 dark:bg-slate-900">
    <div class="max-w-4xl mx-auto px-4 py-6">
      <!-- Modern Header with Gradient -->
      <div class="relative overflow-hidden rounded-2xl mb-8" style="background: linear-gradient(135deg, #EA580C 0%, #DC2626 50%, #B91C1C 100%);">
        <!-- Decorative Background Elements -->
        <div class="absolute inset-0 opacity-10">
          <div class="absolute top-4 right-4 w-32 h-32 rounded-full bg-white/20"></div>
          <div class="absolute -top-8 -right-8 w-24 h-24 rounded-full bg-white/10"></div>
          <div class="absolute bottom-4 left-4 w-20 h-20 rounded-full bg-white/15"></div>
        </div>
        
        <div class="relative p-8">
          <div class="flex items-center gap-4">
            <div class="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg">
              <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
              </svg>
            </div>
            <div>
              <h1 class="text-2xl font-bold text-white">{{ directT('settings.changePassword.title') }}</h1>
              <p class="text-white/90 text-lg font-medium">{{ directT('settings.changePassword.subtitle') }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Navigation Tabs -->
      <div class="mb-8">
        <SettingsNav />
      </div>

      <!-- Modern Password Form -->
      <div class="bg-white dark:bg-slate-800 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-slate-700">
        <div class="p-6">
          <!-- Security Section Header -->
          <div class="flex items-center gap-3 mb-6">
            <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-100 to-red-100 dark:from-orange-900/20 dark:to-red-900/20 flex items-center justify-center shadow-inner">
              <svg class="w-5 h-5 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
              </svg>
            </div>
            <div>
              <h2 class="text-xl font-semibold text-slate-900 dark:text-white">{{ directT('settings.changePassword.securitySettings') }}</h2>
              <p class="text-slate-600 dark:text-slate-300">{{ directT('settings.changePassword.securityDescription') }}</p>
            </div>
          </div>

          <form @submit.prevent="changePassword" class="space-y-6">
            <!-- Current Password Section -->
            <div class="space-y-3">
              <label class="block text-sm font-semibold text-slate-700 dark:text-slate-300">
                {{ directT('settings.changePassword.oldPassword') }}
              </label>
              <div class="relative">
                <input
                  v-model="oldPassword"
                  :type="showPassword1 ? 'text' : 'password'"
                  required
                  class="w-full px-4 py-3 pr-12 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-slate-900 dark:text-white transition-all duration-200 shadow-sm placeholder:text-slate-400"
                  :placeholder="directT('settings.changePassword.typePlaceholder')"
                />
                <button
                  type="button"
                  @click="togglePasswordVisibility(1)"
                  class="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
                >
                  <svg v-if="showPassword1" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                  </svg>
                  <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"></path>
                  </svg>
                </button>
              </div>
            </div>

            <!-- New Password Section -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div class="space-y-3">
                <label class="block text-sm font-semibold text-slate-700 dark:text-slate-300">
                  {{ directT('settings.changePassword.newPassword') }}
                </label>
                <div class="relative">
                  <input
                    v-model="newPassword"
                    :type="showPassword2 ? 'text' : 'password'"
                    required
                    minlength="6"
                    class="w-full px-4 py-3 pr-12 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-slate-900 dark:text-white transition-all duration-200 shadow-sm placeholder:text-slate-400"
                    :placeholder="directT('settings.changePassword.typePlaceholder')"
                  />
                  <button
                    type="button"
                    @click="togglePasswordVisibility(2)"
                    class="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
                  >
                    <svg v-if="showPassword2" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 616 0z"></path>
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                    </svg>
                    <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"></path>
                    </svg>
                  </button>
                </div>
              </div>

              <!-- Confirm Password -->
              <div class="space-y-3">
                <label class="block text-sm font-semibold text-slate-700 dark:text-slate-300">
                  {{ directT('settings.changePassword.confirmPassword') }}
                </label>
                <div class="relative">
                  <input
                    v-model="confirmPassword"
                    :type="showPassword3 ? 'text' : 'password'"
                    required
                    minlength="6"
                    class="w-full px-4 py-3 pr-12 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-slate-900 dark:text-white transition-all duration-200 shadow-sm placeholder:text-slate-400"
                    :placeholder="directT('settings.changePassword.typePlaceholder')"
                  />
                  <button
                    type="button"
                    @click="togglePasswordVisibility(3)"
                    class="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
                  >
                    <svg v-if="showPassword3" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 616 0z"></path>
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                    </svg>
                    <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"></path>
                    </svg>
                  </button>
                </div>
                <p v-if="passwordMismatch" class="text-sm text-red-600 dark:text-red-400 flex items-center gap-2 bg-red-50 dark:bg-red-900/20 p-2 rounded-lg">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  {{ directT('settings.changePassword.passwordsDoNotMatch') }}
                </p>
              </div>
            </div>

            <!-- Status Messages -->
            <div v-if="successMessage || errorMessage" class="space-y-3">
              <div v-if="successMessage" class="p-4 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 rounded-xl border border-emerald-200 dark:border-emerald-800">
                <div class="flex items-center gap-3">
                  <div class="w-8 h-8 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center">
                    <svg class="w-4 h-4 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                  </div>
                  <span class="text-sm font-medium">{{ successMessage }}</span>
                </div>
              </div>
              
              <div v-if="errorMessage" class="p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-xl border border-red-200 dark:border-red-800">
                <div class="flex items-center gap-3">
                  <div class="w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
                    <svg class="w-4 h-4 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </div>
                  <span class="text-sm font-medium">{{ errorMessage }}</span>
                </div>
              </div>
            </div>

            <!-- Security Tips -->
            <div class="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl border border-blue-200 dark:border-blue-800">
              <div class="flex items-start gap-3">
                <div class="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mt-0.5 flex-shrink-0">
                  <svg class="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
                <div class="text-sm text-blue-700 dark:text-blue-300">
                  <p class="font-semibold mb-2">{{ directT('settings.changePassword.passwordSecurityTips') }}</p>
                  <ul class="text-sm space-y-1 text-blue-600 dark:text-blue-400">
                    <li class="flex items-center gap-2">
                      <div class="w-1.5 h-1.5 rounded-full bg-blue-400"></div>
                      {{ directT('settings.changePassword.tip1') }}
                    </li>
                    <li class="flex items-center gap-2">
                      <div class="w-1.5 h-1.5 rounded-full bg-blue-400"></div>
                      {{ directT('settings.changePassword.tip2') }}
                    </li>
                    <li class="flex items-center gap-2">
                      <div class="w-1.5 h-1.5 rounded-full bg-blue-400"></div>
                      {{ directT('settings.changePassword.tip3') }}
                    </li>
                    <li class="flex items-center gap-2">
                      <div class="w-1.5 h-1.5 rounded-full bg-blue-400"></div>
                      {{ directT('settings.changePassword.tip4') }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="flex items-center justify-between pt-6 border-t border-gray-200 dark:border-slate-600">
              <RouterLink
                to="/authentication/forgot-password"
                class="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 transition-colors font-medium"
              >
                {{ directT('settings.changePassword.forgotPassword') }}
              </RouterLink>

              <button
                type="submit"
                :disabled="isLoading || passwordMismatch"
                class="px-6 py-3 text-sm font-medium rounded-xl transition-all duration-200 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:hover:scale-100"
                :style="{
                  color: '#ffffff',
                  background: isLoading || passwordMismatch ? '#9CA3AF' : 'linear-gradient(to right, #f97316, #dc2626)',
                  opacity: isLoading || passwordMismatch ? '0.5' : '1'
                }"
                @mouseover="!isLoading && !passwordMismatch && ($event.target.style.background = 'linear-gradient(to right, #ea580c, #b91c1c)')"
                @mouseout="!isLoading && !passwordMismatch && ($event.target.style.background = 'linear-gradient(to right, #f97316, #dc2626)')"
              >
                <svg v-if="!isLoading" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <svg v-else class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ isLoading ? directT('settings.changePassword.updating') : directT('settings.changePassword.updatePassword') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from "vue";
import { RouterLink } from 'vue-router';
import SettingsNav from "../SettingsNav.vue";
import { supabase } from '@/utils/supabase';
import { useI18n } from "vue-i18n";
import enTranslations from "@/i18n/locales/en.json";
import heTranslations from "@/i18n/locales/he.json";
import arTranslations from "@/i18n/locales/ar.json";

type TranslationRecord = Record<string, any>;
type TranslationsType = Record<string, TranslationRecord>;

const allTranslations: TranslationsType = {
  en: enTranslations,
  he: heTranslations,
  ar: arTranslations,
};

export default defineComponent({
  name: "ChangePassword",
  components: {
    SettingsNav,
    RouterLink
  },
  setup() {
    const { t, locale } = useI18n();

    // Direct translation function with fallback
    function directT(key: string): string {
      const parts = key.split('.');
      let result: any = allTranslations[locale.value] || allTranslations.en;
      
      for (const part of parts) {
        if (result && typeof result === 'object' && part in result) {
          result = result[part];
        } else {
          // Fallback to English if key doesn't exist
          let englishResult = allTranslations.en;
          for (const engPart of parts) {
            if (englishResult && typeof englishResult === 'object' && engPart in englishResult) {
              englishResult = englishResult[engPart];
            } else {
              return key; // Return the key itself if not found in English either
            }
          }
          return typeof englishResult === 'string' ? englishResult : key;
        }
      }
      
      return typeof result === 'string' ? result : key;
    }

    // משתנים לשמירת הסיסמאות
    const oldPassword = ref('');
    const newPassword = ref('');
    const confirmPassword = ref('');
    
    // משתנים לניהול מצב הצגת הסיסמה
    const showPassword1 = ref(false);
    const showPassword2 = ref(false);
    const showPassword3 = ref(false);
    
    // משתנים לניהול מצב הטופס
    const isLoading = ref(false);
    const successMessage = ref('');
    const errorMessage = ref('');
    
    // חישוב שגיאת אי התאמה בין הסיסמאות
    const passwordMismatch = computed(() => {
      return newPassword.value && confirmPassword.value && 
             newPassword.value !== confirmPassword.value;
    });
    
    // פונקציה להחלפת מצב הצגת הסיסמה
    const togglePasswordVisibility = (passwordField: number) => {
      if (passwordField === 1) showPassword1.value = !showPassword1.value;
      else if (passwordField === 2) showPassword2.value = !showPassword2.value;
      else if (passwordField === 3) showPassword3.value = !showPassword3.value;
    };
    
    // פונקציה לשינוי הסיסמה
    const changePassword = async () => {
      // איפוס הודעות
      successMessage.value = '';
      errorMessage.value = '';
      
      // בדיקה שהסיסמאות החדשות תואמות
      if (newPassword.value !== confirmPassword.value) {
        errorMessage.value = directT('settings.changePassword.errorPasswordsMismatch');
        return;
      }
      
      // בדיקת אורך סיסמה מינימלי
      if (newPassword.value.length < 6) {
        errorMessage.value = directT('settings.changePassword.errorPasswordLength');
        return;
      }
      
      try {
        isLoading.value = true;
        
        // קבלת המשתמש הנוכחי
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          errorMessage.value = directT('settings.changePassword.errorUserNotLoggedIn');
          return;
        }
        
        // התחברות מחדש עם הסיסמה הנוכחית כדי לוודא שהיא נכונה
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: session.user.email || '',
          password: oldPassword.value,
        });
        
        if (signInError) {
          errorMessage.value = directT('settings.changePassword.errorWrongCurrentPassword');
          return;
        }
        
        // שינוי הסיסמה
        const { error: updateError } = await supabase.auth.updateUser({
          password: newPassword.value,
        });
        
        if (updateError) {
          errorMessage.value = directT('settings.changePassword.errorUpdatingPassword') + ': ' + updateError.message;
          return;
        }
        
        // איפוס הטופס
        oldPassword.value = '';
        newPassword.value = '';
        confirmPassword.value = '';
        
        successMessage.value = directT('settings.changePassword.successMessage');
      } catch (error) {
        console.error('שגיאה בעדכון סיסמה:', error);
        errorMessage.value = directT('settings.changePassword.errorUnexpected');
      } finally {
        isLoading.value = false;
      }
    };
    
    return {
      oldPassword,
      newPassword,
      confirmPassword,
      showPassword1,
      showPassword2,
      showPassword3,
      isLoading,
      successMessage,
      errorMessage,
      passwordMismatch,
      togglePasswordVisibility,
      changePassword,
      directT
    };
  },
});
</script>

<style scoped>
/* Dark mode support */
.dark label {
  color: #f1f5f9 !important;
}

.dark .form-group label {
  color: #f1f5f9 !important;
}

.dark .color-control label {
  color: #f1f5f9 !important;
}

.dark input[type="text"],
.dark input[type="email"],
.dark input[type="tel"],
.dark input[type="number"],
.dark input[type="date"],
.dark input[type="time"],
.dark input[type="password"],
.dark input[type="file"],
.dark select,
.dark textarea {
  background-color: #475569 !important;
  border-color: #64748b !important;
  color: #f1f5f9 !important;
}

.dark input[type="text"]:focus,
.dark input[type="email"]:focus,
.dark input[type="tel"]:focus,
.dark input[type="number"]:focus,
.dark input[type="date"]:focus,
.dark input[type="time"]:focus,
.dark input[type="password"]:focus,
.dark input[type="file"]:focus,
.dark select:focus,
.dark textarea:focus {
  border-color: #EA580C !important;
  box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.1) !important;
}

.dark input[type="text"]::placeholder,
.dark input[type="email"]::placeholder,
.dark input[type="tel"]::placeholder,
.dark input[type="number"]::placeholder,
.dark input[type="password"]::placeholder,
.dark textarea::placeholder {
  color: #94a3b8 !important;
}

.dark select {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23f1f5f9' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e") !important;
  background-repeat: no-repeat !important;
  background-position: right 0.5rem center !important;
  background-size: 1.5em 1.5em !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  appearance: none !important;
  padding-right: 2.5rem !important;
}

.dark input:disabled,
.dark select:disabled,
.dark textarea:disabled {
  background-color: #374151 !important;
  color: #9ca3af !important;
  cursor: not-allowed !important;
}

/* General dark mode overrides */
.dark .bg-white {
  background-color: #1e293b !important;
}

.dark .bg-gray-50 {
  background-color: #374151 !important;
}

.dark .bg-gray-100 {
  background-color: #475569 !important;
}

.dark .border-gray-100 {
  border-color: #374151 !important;
}

.dark .border-gray-200 {
  border-color: #475569 !important;
}

.dark .border-gray-300 {
  border-color: #64748b !important;
}

.dark .text-slate-900 {
  color: #f1f5f9 !important;
}

.dark .text-slate-800 {
  color: #f1f5f9 !important;
}

.dark .text-slate-700 {
  color: #e2e8f0 !important;
}

.dark .text-slate-600 {
  color: #cbd5e1 !important;
}

.dark .text-slate-500 {
  color: #94a3b8 !important;
}

.dark .text-slate-400 {
  color: #64748b !important;
}

.dark .text-slate-300 {
  color: #cbd5e1 !important;
}

.dark .text-gray-900 {
  color: #f1f5f9 !important;
}

.dark .text-gray-800 {
  color: #f1f5f9 !important;
}

.dark .text-gray-700 {
  color: #e2e8f0 !important;
}

.dark .text-gray-600 {
  color: #cbd5e1 !important;
}

.dark .text-gray-500 {
  color: #94a3b8 !important;
}

.dark .text-gray-400 {
  color: #64748b !important;
}

/* Card backgrounds */
.dark .bg-slate-800 {
  background-color: #1e293b !important;
}

.dark .bg-slate-900 {
  background-color: #0f172a !important;
}

.dark .bg-slate-700 {
  background-color: #334155 !important;
}

.dark .bg-slate-600 {
  background-color: #475569 !important;
}

/* Border colors */
.dark .border-slate-700 {
  border-color: #334155 !important;
}

.dark .border-slate-600 {
  border-color: #475569 !important;
}

/* Gradient backgrounds for icons */
.dark .from-orange-100 {
  --tw-gradient-from: rgba(234, 88, 12, 0.2) !important;
}

.dark .to-red-100 {
  --tw-gradient-to: rgba(220, 38, 38, 0.2) !important;
}

.dark .from-orange-900\/20 {
  --tw-gradient-from: rgba(154, 52, 18, 0.2) !important;
}

.dark .to-red-900\/20 {
  --tw-gradient-to: rgba(127, 29, 29, 0.2) !important;
}

/* Icon colors */
.dark .text-orange-600 {
  color: #ea580c !important;
}

.dark .text-orange-400 {
  color: #fb923c !important;
}

/* Password visibility button styling */
.dark .hover\:bg-slate-100:hover {
  background-color: #475569 !important;
}

.dark .hover\:bg-slate-700:hover {
  background-color: #475569 !important;
}

.dark .hover\:text-slate-600:hover {
  color: #cbd5e1 !important;
}

.dark .hover\:text-slate-300:hover {
  color: #e2e8f0 !important;
}

/* Shadow adjustments for dark mode */
.dark .shadow-xl {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2) !important;
}

.dark .shadow-lg {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2) !important;
}

.dark .shadow-sm {
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.2) !important;
}

.dark .shadow-md {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2) !important;
}

.dark .shadow-inner {
  box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.2) !important;
}

/* Hover effects for dark mode */
.dark .hover\:shadow-xl:hover {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2) !important;
}

.dark .hover\:shadow-md:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2) !important;
}

/* Status message backgrounds */
.dark .bg-emerald-50 {
  background-color: rgba(16, 185, 129, 0.1) !important;
}

.dark .bg-red-50 {
  background-color: rgba(239, 68, 68, 0.1) !important;
}

.dark .bg-blue-50 {
  background-color: rgba(59, 130, 246, 0.1) !important;
}

.dark .border-emerald-200 {
  border-color: rgba(16, 185, 129, 0.3) !important;
}

.dark .border-red-200 {
  border-color: rgba(239, 68, 68, 0.3) !important;
}

.dark .border-blue-200 {
  border-color: rgba(59, 130, 246, 0.3) !important;
}

.dark .border-emerald-800 {
  border-color: rgba(16, 185, 129, 0.5) !important;
}

.dark .border-red-800 {
  border-color: rgba(239, 68, 68, 0.5) !important;
}

.dark .border-blue-800 {
  border-color: rgba(59, 130, 246, 0.5) !important;
}

.dark .text-emerald-700 {
  color: #10b981 !important;
}

.dark .text-emerald-400 {
  color: #34d399 !important;
}

.dark .text-red-700 {
  color: #ef4444 !important;
}

.dark .text-red-400 {
  color: #f87171 !important;
}

.dark .text-red-600 {
  color: #dc2626 !important;
}

.dark .text-blue-700 {
  color: #1d4ed8 !important;
}

.dark .text-blue-600 {
  color: #2563eb !important;
}

.dark .text-blue-400 {
  color: #60a5fa !important;
}

.dark .text-blue-300 {
  color: #93c5fd !important;
}

/* Status message icon backgrounds */
.dark .bg-emerald-100 {
  background-color: rgba(16, 185, 129, 0.2) !important;
}

.dark .bg-red-100 {
  background-color: rgba(239, 68, 68, 0.2) !important;
}

.dark .bg-blue-100 {
  background-color: rgba(59, 130, 246, 0.2) !important;
}

.dark .bg-emerald-900\/30 {
  background-color: rgba(6, 78, 59, 0.3) !important;
}

.dark .bg-red-900\/30 {
  background-color: rgba(127, 29, 29, 0.3) !important;
}

.dark .bg-blue-900\/30 {
  background-color: rgba(30, 58, 138, 0.3) !important;
}

/* Security tips bullet points */
.dark .bg-blue-400 {
  background-color: #60a5fa !important;
}

/* RouterLink styling */
.dark .hover\:text-slate-700:hover {
  color: #e2e8f0 !important;
}

/* Ensure all text elements have proper contrast */
.dark h1,
.dark h2,
.dark h3,
.dark h4,
.dark h5,
.dark h6 {
  color: #f1f5f9 !important;
}

.dark p {
  color: #cbd5e1 !important;
}

.dark span {
  color: inherit !important;
}

.dark ul {
  color: inherit !important;
}

.dark li {
  color: inherit !important;
}

/* Override any remaining white backgrounds */
.dark [class*="bg-white"] {
  background-color: #1e293b !important;
}

.dark [class*="bg-gray-50"] {
  background-color: #0f172a !important;
}

.dark [class*="bg-gray-100"] {
  background-color: #475569 !important;
}

/* Button keeps its original gradient colors in dark mode */
</style>
